import { has } from 'lodash';
import { createWrapper } from 'src/careflow/ph-core/fhir-client/fhir-react/base';
import { getTableRowDueTo } from 'src/careflow/common/common-utils';

export const ServiceRequestWrapper = createWrapper('ServiceRequest', (serviceRequest) => ({
  getIdFromReference(reference: string): string {
    return reference.split('/')?.[1];
  },

  getCasesIds(): string[] {
    if (serviceRequest?.supportingInfo && serviceRequest.supportingInfo.length > 0) {
      const cases = serviceRequest.supportingInfo
        .filter((s) => has(s, 'reference') && s?.reference)
        .filter((s) => s.reference?.startsWith('CarePlan/'))
        .map((s) => (s.reference ? this.getIdFromReference(s.reference) : ''));
      return cases;
    }
    return [];
  },

  hasCases(): boolean {
    return this.getCasesIds().length > 0;
  },

  hasHealthcareServices(): boolean {
    return this.getHealthcareServices().length > 0;
  },

  getDocumentReferenceIds(): string[] {
    if (serviceRequest?.supportingInfo && serviceRequest.supportingInfo.length > 0) {
      const documents = serviceRequest.supportingInfo
        .filter((s) => has(s, 'reference') && s?.reference)
        .filter((s) => s.reference?.startsWith('DocumentReference/'))
        .map((s) => (s.reference ? this.getIdFromReference(s.reference) : ''));
      return documents;
    }
    return [];
  },

  getCoverageIds(): string[] {
    if (serviceRequest?.insurance && serviceRequest.insurance.length > 0) {
      const coverages = serviceRequest.insurance
        .filter((s) => has(s, 'reference') && s?.reference)
        .filter((s) => s.reference?.startsWith('Coverage/'))
        .map((s) => (s.reference ? this.getIdFromReference(s.reference) : ''));
      return coverages;
    }
    return [];
  },

  hasCoverages(): boolean {
    return this.getCoverageIds().length > 0;
  },

  getHealthcareServiceIds(): string[] {
    if (serviceRequest?.performer && serviceRequest?.performer.length > 0) {
      const healthcareServices = serviceRequest.performer
        .filter((s) => has(s, 'reference') && s?.reference)
        .filter((s) => s.reference?.startsWith('HealthcareService/'))
        .map((s) => (s.reference ? this.getIdFromReference(s.reference) : ''));
      return healthcareServices;
    }
    return [];
  },

  getHealthcareServices(): any[] {
    if (serviceRequest?.performer && serviceRequest?.performer.length > 0) {
      const healthcareServices = serviceRequest.performer.filter(
        (s) => s.reference?.startsWith('HealthcareService/') || s.type === 'HealthcareService'
      );
      return healthcareServices;
    }
    return [];
  },

  getPastDue(): string {
    const result = getTableRowDueTo(
      new Date(serviceRequest?.occurrencePeriod?.end || ''),
      serviceRequest?.status
    ).subStatus;
    return result;
  },

  getPerfomerByType(type: string) {
    if (serviceRequest?.performer && serviceRequest?.performer.length > 0) {
      const performer = serviceRequest.performer.find((s) => s.type === type);
      return performer;
    }
    return null;
  },
  getInternalNumber: (SERVICEREQUEST_INTERNAL_NUMBER_IDENTIFIER_SYSTEM: string): string | null =>
    serviceRequest?.identifier?.find?.(
      ({ system }) => system === SERVICEREQUEST_INTERNAL_NUMBER_IDENTIFIER_SYSTEM
    )?.value ?? null,
  getPerformersByReferenceType: (referenceType: string) =>
    serviceRequest?.performer?.filter?.(
      ({ reference }) => !!reference?.startsWith?.(referenceType)
    ),
  getFirstCategory: () => serviceRequest?.category?.[0]?.coding?.[0],
  getRequesterDisplay: () => serviceRequest?.requester?.display,
  getServiceName: () => serviceRequest?.code?.coding?.[0]?.display,
  getServiceLocationName: () => {
    // TODO: check if the filter logic is correct
    // is correct that Practitioners are the providedBy?
    const performer = serviceRequest?.performer?.find?.((performer) =>
      performer?.reference?.includes('HealthcareService')
    );

    if (performer?.display) {
      return performer?.display;
    }

    return undefined;
  },

  getServiceAssignedToName: () => {
    const performer =
      serviceRequest?.performer?.find?.((performer) =>
        performer?.reference?.includes('PractitionerRole')
      ) ||
      serviceRequest?.performer?.find?.((performer) =>
        performer?.reference?.includes('Practitioner')
      ) ||
      serviceRequest?.performer?.find?.((performer) =>
        performer?.reference?.includes('Organization')
      );

    if (performer?.display) {
      return performer?.display;
    }

    return undefined;
  },
}));
export type WrappedServiceRequest = ReturnType<typeof ServiceRequestWrapper>;
